import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * ComingSoonWrapper — modern, rich, and cool overlay (green theme)
 */
export default function ComingSoonWrapper({
  children,
  title = "🚧 Coming Soon",
  subtitle = "This feature is under active development.",
  eta,
  showNotify = false,
  onNotify,
  className = "",
}) {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);

  const canNotify = showNotify && typeof onNotify === "function";

  const bgId = useMemo(() => `noise-${Math.random().toString(36).slice(2)}`,[ ]);

  const handleNotify = async (e) => {
    e?.preventDefault();
    if (!canNotify || !email) return;
    try {
      await onNotify(email);
      setSent(true);
      setEmail("");
    } catch {
      // no-op
    }
  };

  return (
    <div className={`relative ${className}`}>
      {/* Underlying page (kept visible but softened) */}
      <div className="blur-[2px] opacity-70 pointer-events-none select-none">
        {children}
      </div>

      {/* Decorative background */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-gradient-to-br from-emerald-500 via-indigo-500 to-blue-500 opacity-30 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-gradient-to-tr from-emerald-400 via-cyan-400 to-sky-500 opacity-30 blur-3xl" />
        <div className="absolute inset-0 mix-blend-overlay opacity-30" style={{ backgroundImage: `url(#${bgId})` }} />
      </div>

      {/* Overlay */}
      <div className="absolute inset-0 grid place-items-center bg-black/50 backdrop-blur-sm">
        <AnimatePresence>
          <motion.div
            role="dialog"
            aria-modal="true"
            initial={{ y: 12, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 8, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-[92%] max-w-xl"
          >
            {/* Neon gradient frame */}
            <div className="p-[2px] rounded-2xl bg-gradient-to-r from-emerald-500 via-indigo-500 to-cyan-400 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.5)]">
              {/* Glass card */}
              <div className="rounded-2xl bg-white/80 dark:bg-slate-900/70 backdrop-blur-xl border border-white/40 dark:border-white/10">
                <div className="px-6 sm:px-8 py-6">
                  {/* Badge */}
                  <div className="inline-flex items-center gap-2 rounded-full bg-black/5 dark:bg-white/10 px-3 py-1 text-xs font-medium text-gray-700 dark:text-gray-200">
                    <PulseDot /> New
                  </div>

                  {/* Title */}
                  <h2 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-gray-900 dark:text-white">
                    {title}
                  </h2>

                  {/* Subtitle + ETA */}
                  <p className="mt-2 text-sm sm:text-base text-gray-600 dark:text-gray-300">
                    {subtitle}
                  </p>
                  {eta && (
                    <p className="mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                      {eta}
                    </p>
                  )}

                  {/* Progress shimmer line */}
                  <div className="mt-5 h-1.5 w-full overflow-hidden rounded-full bg-gray-200/70 dark:bg-white/10">
                    <div className="h-full w-1/3 animate-shimmer rounded-full bg-gradient-to-r from-transparent via-gray-900/40 dark:via-white/50 to-transparent" />
                  </div>

                  {/* Actions */}
                  <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3">
                    <a
                      href="#"
                      onClick={(e)=> e.preventDefault()}
                      className="group inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-emerald-600 shadow-lg shadow-emerald-600/20 hover:shadow-emerald-600/40 active:scale-[0.99] transition"
                    >
                      <SparkleIcon className="mr-2 h-4 w-4" /> Notify Me
                    </a>

                    <a
                      href="/dashboard"
                      className="inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-medium text-gray-800 dark:text-gray-100 bg-white dark:bg-slate-800 border border-gray-200/60 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-slate-700 transition"
                    >
                      <ArrowLeft className="mr-2 h-4 w-4" /> Back to Dashboard
                    </a>
                  </div>

                  {/* Optional inline email form */}
                  {canNotify && (
                    <form onSubmit={handleNotify} className="mt-4 flex items-center gap-2">
                      <input
                        type="email"
                        value={email}
                        onChange={(e)=>setEmail(e.target.value)}
                        placeholder="your@email.com"
                        className="flex-1 rounded-xl border border-gray-300/70 dark:border-white/10 bg-white/80 dark:bg-slate-800/70 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500/50"
                        required
                      />
                      <button
                        type="submit"
                        className="rounded-xl px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 active:scale-[0.99]"
                      >
                        {sent ? "Thanks!" : "Notify"}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Local styles for shimmer + subtle noise */}
      <style>{`
        @keyframes shimmer { 0% { transform: translateX(-100%);} 100% { transform: translateX(300%);} }
        .animate-shimmer { animation: shimmer 2.2s ease-in-out infinite; }
      `}</style>

      {/* Tiny SVG noise pattern (unique per instance) */}
      <svg width="0" height="0">
        <defs>
          <pattern id={"${bgId}"} width="200" height="200" patternUnits="userSpaceOnUse">
            <image href="data:image/svg+xml;utf8,${encodeURIComponent(generateNoiseSVG())}" width="200" height="200" />
          </pattern>
        </defs>
      </svg>
    </div>
  );
}

// ---------- Small UI bits ----------
function PulseDot() {
  return (
    <span className="relative inline-flex h-2.5 w-2.5">
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-30"></span>
      <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500"></span>
    </span>
  );
}

function SparkleIcon({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 2l1.9 4.6L18 8l-4.1 1.4L12 14l-1.9-4.6L6 8l4.1-1.4L12 2zm7 9l1.1 2.6L23 15l-2.9.4L19 18l-.4-2.9L16 15l2.9-1.4L19 11zM2 13l1.1 2.6L6 16l-2.9.4L2 19l-.4-2.9L-1 16l2.9-1.4L2 13z" />
    </svg>
  );
}

function ArrowLeft({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M14 7l-5 5 5 5V7z" />
    </svg>
  );
}

// Tiny SVG noise generator (no external assets)
function generateNoiseSVG() {
  const size = 200;
  let dots = "";
  for (let i = 0; i < 120; i++) {
    const x = Math.random() * size;
    const y = Math.random() * size;
    const o = Math.random() * 0.15 + 0.05;
    const r = Math.random() * 1.2 + 0.2;
    dots += `<circle cx="${x.toFixed(2)}" cy="${y.toFixed(2)}" r="${r.toFixed(2)}" fill="rgba(0,0,0,${o.toFixed(2)})" />`;
  }
  return `<svg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}'>${dots}</svg>`;
}

// Example usage:
// <ComingSoonWrapper title="AI Sequences" subtitle="We’re putting the final polish on this." eta="ETA: Oct 10" showNotify onNotify={(email)=>Promise.resolve(console.log(email))}>
//   <YourPage />
// </ComingSoonWrapper>
